import React, {useContext, useEffect, useState} from "react";
import {Checkbox, Form, Input, Select, Alert, Divider} from "antd";
import Button from "../../common/Button";
import {useLocation, useSearchParams} from "wouter";
import {BrandContext} from "../../common/BrandContext";
import axios from "../../common/axios";
import {NavigationContext} from "../../common/NavigationContext";
import {DeleteRounded} from "@mui/icons-material";
import PopConfirm from "../../common/PopConfirm";
import CircularLoader from "../../common/CircularLoader";

const EditClassDrawer = () => {
    const [options, setOptions] = useState({});
    const [formError, setFormError] = useState(null);
    const brandConfig = useContext(BrandContext);
    const [addClassForm] = Form.useForm();
    const [searchParams, setSearchParams] = useSearchParams();
    const [classId, setClassId] = useState(null);
    const [schoolClasses, setSchoolClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const { setDrawerVisible } = useContext(NavigationContext);
    const [location, navigate] = useLocation();
    const [loading, setLoading] = useState(true);

    // Get classId from search parameters
    useEffect(() => {
        const editClassId = searchParams.get('editClassId');
        if (editClassId) {
            setClassId(parseInt(editClassId, 10));
        }
    }, [searchParams]);

    useEffect(() => {
        setLoading(true);
        axios.get('/web/ui/sc/classes')
            .then(response => {
                const data = response.data;
                const classesArray = Object.values(data);
                setSchoolClasses(classesArray);

                // Set the default selected class based on classId from search params
                if (classId) {
                    const defaultClass = classesArray.find(cls => cls.classId === classId);
                    if (defaultClass) {
                        setSelectedClass(defaultClass);
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching school classes:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [classId]);

    const closeDialog = () => {
        setDrawerVisible(false);
        setFormError(null);
    };

    useEffect(() => {
        if (open) {
            axios.get('/web/ui/sc/getOptions')
                .then(response => {
                    setOptions(response.data);
                })
                .catch(error => {
                    console.error("Failed to fetch options", error);
                });
        }
    }, [selectedClass]);

    useEffect(() => {
        if (Object.keys(options).length !== 0) {
            addClassForm.setFieldsValue({
                clazz: {
                    className: selectedClass?.className,
                    calendarYear: selectedClass?.calendarYear && options?.calenderYears?.find(y => y.value === selectedClass.calendarYear)
                        ? selectedClass.calendarYear
                        : new Date().getFullYear(),
                    schoolYear: selectedClass?.schoolYear && options?.schoolYears?.find(y => y.value === selectedClass.schoolYear)
                        ? selectedClass.schoolYear
                        : options?.schoolYears?.[0]?.value,
                    hideWorkspaces: selectedClass?.hideWorkspaces || false,
                    markingType: brandConfig?.questionClusterMarkingTypes
                        ? brandConfig.questionClusterMarkingTypes[0]
                        : undefined,
                    showSolutions: selectedClass?.showSolutions ?? brandConfig.showSolutions,
                    showWorkedSolutions: selectedClass?.showWorkedSolutions ?? false
                }
            });
        }
    }, [options, selectedClass, brandConfig, addClassForm]);

    const handleSave = async () => {
        setFormError(null);

        try {
            // Validate fields first
            await addClassForm.validateFields();

            // Determine the URL based on whether we are editing or adding
            const url = selectedClass
                ? `/web/ui/sc/updateClass?classId=${selectedClass.classId}`
                : '/web/ui/sc/newClass';

            // Get form values
            const addClassFormValues = addClassForm.getFieldsValue();

            // Make the POST request
            const response = await axios.post(url, addClassFormValues.clazz, {
                headers: { 'Content-Type': 'application/json' }
            });

            // If there's a callback, call it with the response data
            if (typeof callback === "function") {
                callback(response.data);
            }

            // Close the dialog after successful save
            closeDialog();
        } catch (error) {
            // If the error has a validation component, it's already handled by Form.Item rules.
            // Otherwise, handle Axios errors:
            if (error.response?.data?.message) {
                setFormError(error.response.data.message);
            } else if (!error.errorFields) {
                // If it's not a validation error and no message is available
                setFormError('An error occurred.');
            }
        }
    };

    const handleDeleteClass = async () => {
        if (!selectedClass) {
            setFormError('No class selected for deletion.');
            return;
        }

        setFormError(null);

        try {
            await axios.post(`/web/ui/sc/deleteClass?classId=${selectedClass.classId}`, {
                headers: {'Content-Type': 'application/json'}
            });

            // Refresh the classes list after successful deletion
            const response = await axios.get('/web/ui/sc/classes');
            const data = response.data;
            const classesArray = Object.values(data);
            setSchoolClasses(classesArray);

            // Clear the selected class since it's been deleted
            displayToastMessage(`${selectedClass.className} successfully deleted.`, null, 'success', undefined, 10000);
            setSelectedClass(null);

            // Close the drawer after successful deletion
            closeDialog();
            if(location !== '/web/ui/view/classes/schoolClasses'){
                navigate('/web/ui/view/classes/schoolClasses')
            }

        } catch (error) {
            if (error.response?.data?.message) {
                displayToastMessage(error.response.data.message, error, 'error', undefined, 10000);
            } else {
                displayToastMessage('Unable to delete the class. Try the action again.', error, 'error', undefined, 10000);
            }
        }
    };

    if (loading) {
        return <CircularLoader />;
    }

    return (
        <div className={'ch-drawer-right-content'}>
            {formError && (
                <Alert
                    style={{marginBottom: 16}}
                    message="Error"
                    description={formError}
                    type="error"
                    showIcon
                />
            )}
            <h2>{`Edit settings for ${selectedClass?.className}`}</h2>
            <Divider className={'ch-divider'}/>
            {selectedClass &&
                <>
                    <h3>Actions</h3>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                        <PopConfirm
                            title={'Are you sure you want to delete this class?'}
                            okText='Delete Class'
                            onConfirm={handleDeleteClass}>
                                <Button
                                    type={'outline'}
                                    icon={<DeleteRounded/>}
                                >
                                    Delete class
                                </Button>
                        </PopConfirm>
                    </div>
                </>
            }
            <Divider className={'ch-divider'}/>
            <h3>Class Details</h3>
            <Form
                form={addClassForm}
                layout="vertical"
            >
                <Form.Item
                    label="Name"
                    name={['clazz', 'className']}
                    rules={[
                        {required: true, message: 'Enter a classname'}
                    ]}
                    style={{marginBottom: 8}}
                >
                    <Input/>
                </Form.Item>

                {options?.calenderYears && (
                    <Form.Item
                        label="Calendar Year"
                        name={['clazz', 'calendarYear']}
                        style={{marginBottom: 8}}
                    >
                        <Select
                            getPopupContainer={trigger => trigger.parentElement}
                            options={options.calenderYears.map(y => ({value: y.value, label: y.label}))}
                        />
                    </Form.Item>
                )}

                {options?.schoolYears && (
                    <Form.Item
                        label="School Year"
                        name={['clazz', 'schoolYear']}
                        style={{marginBottom: 8}}
                    >
                        <Select
                            getPopupContainer={trigger => trigger.parentElement}
                            options={options.schoolYears.map(y => ({value: y.value, label: y.label})).reverse()}
                        />
                    </Form.Item>
                )}

                {brandConfig?.questionClusterMarkingTypes?.length > 1 && (
                    <Form.Item
                        label="Marking"
                        name={['clazz', 'markingType']}
                        style={{marginBottom: 8}}
                    >
                        <Select
                            getPopupContainer={trigger => trigger.parentElement}
                            options={brandConfig.questionClusterMarkingTypes.map(type => ({value: type, label: type}))}
                        />
                    </Form.Item>
                )}

                {brandConfig?.allowHideWorkspaces && (
                    <Form.Item
                        name={['clazz', 'hideWorkspaces']}
                        valuePropName="checked"
                    >
                        <Checkbox>Hide Workspaces</Checkbox>
                    </Form.Item>
                )}

                {!brandConfig?.hideClassShowSolutions && (
                    <Form.Item
                        name={['clazz', 'showSolutions']}
                        valuePropName="checked"
                    >
                        <Checkbox>Show Answers / Suggested Responses</Checkbox>
                    </Form.Item>
                )}

                {!brandConfig?.hideClassShowWorkedSolutions && (
                    <Form.Item
                        name={['clazz', 'showWorkedSolutions']}
                        valuePropName="checked"
                    >
                        <Checkbox>Show Worked Solutions</Checkbox>
                    </Form.Item>
                )}
                <Form.Item>
                    <Button
                        onClick={() => handleSave()}
                    >
                        {selectedClass ? 'Save Changes' : 'Add Class'}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default EditClassDrawer;